import loadable from '@loadable/component';

import { useUserAccess } from '@/hooks/useUserAccess';
import { useUser } from '@/hooks/useUser';
import { pluralise } from '@/utilities/pluralise';

import PortfolioLinkStatus from '../PortfolioLinkStatus';
import { HoldingsOverview } from '../HoldingsOverview';

import { useHoldingsLimits } from '@/hooks/portfolio/useHoldingsLimits';
import { useSortedHoldings } from '../../hooks/useHoldingsSorting';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import { useFlag } from '@simplywallst/unleash';
import { usePortfolioType } from '../HoldingsTable/hooks';
import { COUNTDOWN_ALERT_CUTOFF_DATE_IN_MS } from '../PortfolioAlerts/constants';

const LimitsAlert = loadable(() => import('../PortfolioAlerts/LimitsAlert'), {
  resolveComponent: (c) => c.LimitsAlert,
});

const CountdownAlert = loadable(
  () => import('../PortfolioAlerts/CountdownAlert'),
  {
    resolveComponent: (c) => c.CountdownAlert,
  }
);

const PortfolioOverview = loadable(() => import('../PortfolioOverview'), {
  resolveComponent: (c) => c.PortfolioOverview,
  fallback: (
    <div className="grid grid-rows-[400px] gap-x2 md:grid-cols-[auto_32%] md:grid-rows-[405px]">
      <div className="min-h-[400px] rounded-x1 bg-legacy-noir" />
      <div className="min-h-[375px] rounded-x1 bg-legacy-noir" />
    </div>
  ),
});

const PortfolioLimitsBanner = loadable(
  () => import('../PortfolioLimitsBanner'),
  {
    resolveComponent: (c) => c.PortfolioLimitsBanner,
  }
);

interface Props {
  portfolio: Portfolio;
}

export const HoldingsPanel = ({ portfolio }: Props) => {
  const flag = useFlag('mono-portfolio-linking-limits');
  const isLimitedLinkedPortfolioEnabled = flag.enabled && flag.ready;

  const { data: userAccess } = useUserAccess();
  const { data: user } = useUser();
  const type = usePortfolioType(portfolio);

  // Include users who signed up before July 17th, 2025
  const isUserSignedUpBeforeCutoff =
    user.registrationDate < COUNTDOWN_ALERT_CUTOFF_DATE_IN_MS;

  const disconnectFlag = useFlag('mono-portfolio-disconnect-countdown');
  const isDisconnectCountdownEnabled =
    disconnectFlag.enabled &&
    disconnectFlag.ready &&
    userAccess.planType === 'free' &&
    type === 'linked-portfolio' &&
    isUserSignedUpBeforeCutoff;

  const { holdings, excludedHoldings } = useHoldingsLimits({
    portfolioHoldings: portfolio.holdings,
  });

  const sortedHoldings = useSortedHoldings(holdings);

  return (
    <div className="grid grid-cols-[100vw] lg:grid-cols-[auto]">
      <ul>
        {isDisconnectCountdownEnabled ? (
          <CountdownAlert />
        ) : isLimitedLinkedPortfolioEnabled ? (
          <LimitsAlert
            portfolio={portfolio}
            excludedHoldingsCount={excludedHoldings.length}
            holdingsCount={holdings.length}
          />
        ) : portfolio.isAnalysisRestricted ? (
          <li className="mb-x2 first:mt-x2 md:first:mt-x0">
            <PortfolioLimitsBanner
              data-cy-id="portfolio-limits-banner-holdings-panel"
              content={`You've reached the portfolio limit on your ${userAccess.planType} plan. To view this portfolio, consider upgrading your plan or removing excess portfolios.`}
            />
          </li>
        ) : excludedHoldings.length > 0 ? (
          <li>
            <PortfolioLimitsBanner
              data-cy-id="excluded-holdings-banner-holdings-panel"
              content={`${excludedHoldings.length} ${pluralise(
                excludedHoldings.length,
                'holding',
                'holdings'
              )} excluded from analysis due to ${
                userAccess.planType
              } plan limit being reached. Upgrade for full access.`}
            />
          </li>
        ) : null}
      </ul>
      <section data-cy-id="holdings-panel-portfolio-overview">
        <PortfolioOverview portfolio={portfolio} />
        <PortfolioLinkStatus portfolio={portfolio} />
      </section>
      <HoldingsOverview
        portfolio={portfolio}
        sortedHoldings={sortedHoldings}
        excludedHoldings={excludedHoldings}
      />
    </div>
  );
};
